import { useCallback, useEffect, useState } from 'react'
import Sheet from './Sheet.jsx'
import {
  LINK_KINDS, addLink, getLinks, linkCandidates, removeLink, resolveLinks,
} from '../lib/notes.js'
import { peso } from '../lib/format.js'
import { useJump } from '../lib/jump.js'

/**
 * Links from one thing to anything else — a payable to the project it is for,
 * a reminder to a board item, a build to the note where he thought of it.
 *
 * A link never moves money and never changes state. It is a cross-reference,
 * which is exactly what makes it safe to link freely.
 *
 * Labels are resolved on read rather than stored, so renaming a project
 * renames it everywhere it appears here with nothing to migrate.
 */
/* Kinds the shell knows how to open. A link to anything else still reads and
   still unlinks; it just is not a doorway. */
const OPENABLE = new Set(['build', 'payable', 'installment', 'account', 'topic', 'project'])

export default function Links({ target, toast, compact }) {
  const jumpTo = useJump()
  const [rows, setRows] = useState(null)
  const [picking, setPicking] = useState(false)

  const load = useCallback(async () => {
    if (!target?.id) { setRows([]); return }
    try {
      setRows(await resolveLinks(await getLinks(target.type, target.id)))
    } catch {
      setRows([])
    }
  }, [target?.type, target?.id])

  useEffect(() => { load() }, [load])

  /* Nothing to link to before the thing itself exists. */
  if (!target?.id) return null
  if (!rows) return null

  /*
   * What this thing has cost so far.
   *
   * He linked two payables to the Vietnam trip and the screen listed them
   * without ever adding them up, which is the one question linking them was
   * meant to answer. Paid and still-owed stay apart: money he has actually
   * put in is a different fact from money he has committed, and a single
   * blended figure would overstate the first.
   */
  const paid = rows.reduce((n, l) => n + (l.money?.settled ? l.money.amount : 0), 0)
  const owed = rows.reduce((n, l) => n + (l.money && !l.money.settled ? l.money.amount : 0), 0)
  /* Two or more, or it is not a total. A single linked payable already shows
     its own figure on its own row, and repeating it underneath as a sum would
     just be the same number twice. */
  const hasMoney = rows.filter((l) => l.money).length > 1

  return (
    <div className={'links' + (compact ? ' compact' : '')}>
      {rows.length ? (
        <>
          <p className="lbl" style={{ margin: '0 0 6px' }}>Linked</p>
          {rows.map((l) => (
            <div className="linkrow" key={l.id}>
              <span className="lbl">{l.kindLabel}</span>
              {jumpTo && OPENABLE.has(l.type) && l.label !== 'No longer there' ? (
                <button
                  type="button"
                  className="lt linkrow-open"
                  onClick={() => jumpTo({ type: l.type, id: l.targetId }, target)}
                  aria-label={'Open ' + l.label}
                >
                  {l.label}
                  {l.detail ? <em className="ldet num">{l.detail}</em> : null}
                  <span className="go" aria-hidden="true">›</span>
                </button>
              ) : (
                <span className={'lt' + (l.label === 'No longer there' ? ' gone' : '')}>
                  {l.label}
                  {/* The figure is the reason he linked it. Without this the row
                      only ever said the name and sent him to another screen. */}
                  {l.detail ? <em className="ldet num">{l.detail}</em> : null}
                </span>
              )}
              <button
                type="button"
                aria-label={'Unlink ' + l.label}
                onClick={async () => {
                  try {
                    await removeLink(l.id)
                    load()
                  } catch (e) {
                    toast?.(e.message || 'Could not unlink that.')
                  }
                }}
              >
                ✕
              </button>
            </div>
          ))}

          {hasMoney ? (
            <div className="linktotal">
              <span className="num">{peso(paid)}</span>
              <span className="lbl">in</span>
              {owed ? (
                <span className="lbl">· {peso(owed)} still owed</span>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      <button type="button" className="linkish" onClick={() => setPicking(true)}>
        {rows.length ? 'Link something else' : 'Link to something'}
      </button>

      {picking ? (
        <PickSheet
          target={target}
          existing={rows}
          onClose={() => setPicking(false)}
          onDone={() => { setPicking(false); load() }}
          toast={toast}
        />
      ) : null}
    </div>
  )
}

function PickSheet({ target, existing, onClose, onDone, toast }) {
  const [kind, setKind] = useState(null)
  const [items, setItems] = useState(null)
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!kind) return
    setItems(null)
    linkCandidates(kind.type).then(setItems).catch(() => setItems([]))
  }, [kind])

  const already = new Set(existing.map((l) => l.type + ':' + l.targetId))
  const shown = (items ?? [])
    .filter((i) => !(i.id === target.id && kind?.type === target.type))
    .filter((i) => !q.trim() || (i.label || '').toLowerCase().includes(q.trim().toLowerCase()))

  return (
    <Sheet title={kind ? 'Pick a ' + kind.label.toLowerCase() : 'Link to what?'} onClose={onClose}>
      {!kind ? (
        <div className="catpick">
          {LINK_KINDS.filter((k) => k.pickable !== false).map((k) => (
            <button key={k.type} type="button" onClick={() => setKind(k)}>
              {k.label}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="field">
            <input
              type="search"
              placeholder={'Search ' + kind.label.toLowerCase() + 's'}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoComplete="off"
            />
          </div>

          {items === null ? <p className="lbl">Loading…</p> : null}
          {items && shown.length === 0 ? (
            <p className="lbl">Nothing here to link to.</p>
          ) : null}

          <div className="catpick">
            {shown.map((i) => {
              const done = already.has(kind.type + ':' + i.id)
              return (
                <button
                  key={i.id}
                  type="button"
                  disabled={busy || done}
                  style={done ? { opacity: 0.45 } : undefined}
                  onClick={async () => {
                    setBusy(true)
                    try {
                      await addLink(target, { type: kind.type, id: i.id })
                      onDone()
                    } catch (e) {
                      toast?.(e.message || 'Could not link that.')
                    } finally {
                      setBusy(false)
                    }
                  }}
                >
                  {i.label || 'Untitled'}
                  {done ? <em style={{ marginLeft: 'auto', fontStyle: 'normal' }} className="lbl">linked</em> : null}
                </button>
              )
            })}
          </div>

          <button type="button" className="btn ghost" style={{ marginTop: 14 }}
            onClick={() => { setKind(null); setQ('') }}>
            Back
          </button>
        </>
      )}
    </Sheet>
  )
}
